import * as React from 'react'
import { Outlet, useParams } from 'react-router-dom'

import { Features } from 'generated/featureDefinitions'
import moment from 'moment'

import { observer } from 'mobx-react'

import { Employee, useCurrentSession } from 'modules/core/hooks/useCurrentSession'

import ClockIn from 'modules/attendance/components/ClockIn'
import ClockInStore from 'modules/attendance/components/ClockIn/Store'
import type { StoreProps } from 'modules/attendance/components/ClockIn/types'
import {
  Cycle,
  useAttendanceDateNavigator,
} from 'modules/attendance/lib/attendanceDateNavigatorContext'
import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import {
  AttendancePoliciesProvider,
  useAttendancePolicies,
} from 'modules/attendance/lib/attendancePolicies'
import { ContractsVersionProvider } from 'modules/attendance/lib/contractsVersionContext'

const PeriodContainer = observer(({ employee }: { employee: Employee }) => {
  const params = useParams()
  const { featureAvailability: projectsFeatureAvailability } = useAttendanceFeatures(
    Features.projects_management
  )
  const { dateFrom, dateTo, cycle } = useAttendanceDateNavigator()
  const { data } = useAttendancePolicies()

  const canReadOwnedAttendance = data?.employee?._policies?.seeAttendance.can ?? false
  const employeeId = String(employee?.id)

  const getNewStore = React.useCallback((): ClockInStore => {
    const coercedProps: StoreProps = {
      year: Number(params.year),
      month: Number(params.month),
      employeeId,
      currentEmployeeId: employeeId,
      layout: 'employee',
      hasNewProjectsSection: projectsFeatureAvailability,
    }

    return new ClockInStore(coercedProps)
  }, [params.year, params.month, employeeId, projectsFeatureAvailability])

  const [store, setStore] = React.useState<ClockInStore>(() => getNewStore())
  const [loading, setLoading] = React.useState(true)

  const storeRef = React.useRef(store)
  storeRef.current = store

  const fetchData = React.useCallback(
    async (storeToFetch: ClockInStore) => {
      setLoading(true)
      await storeToFetch.fetch(moment(dateFrom), moment(dateTo))
      setLoading(false)
    },
    [dateFrom, dateTo]
  )

  // Initial fetch on mount
  React.useEffect(() => {
    fetchData(store)
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const isInitialMount = React.useRef(true)
  const prevCycleRef = React.useRef<Cycle>(cycle)
  const prevDateFromRef = React.useRef<Date>(dateFrom)
  const prevDateToRef = React.useRef<Date>(dateTo)
  const prevYearRef = React.useRef<string | undefined>(params.year)
  const prevMonthRef = React.useRef<string | undefined>(params.month)

  React.useEffect(() => {
    const coercedParams = {
      year: Number(params.year),
      month: Number(params.month),
    }

    const currentStore = storeRef.current
    const isMonthChanged = currentStore.monthChanged(coercedParams)
    const isWeekChanged = cycle === Cycle.weekly && prevDateFromRef.current !== dateFrom
    const isCycleChanged = cycle !== prevCycleRef.current

    if (isMonthChanged || isWeekChanged || isCycleChanged) {
      const newStore = getNewStore()
      setStore(newStore)
      prevCycleRef.current = cycle
      prevDateFromRef.current = dateFrom
      prevDateToRef.current = dateTo
      prevYearRef.current = params.year
      prevMonthRef.current = params.month
      fetchData(newStore)
    }
  }, [params.year, params.month, cycle, dateFrom, getNewStore, fetchData]) // eslint-disable-line react-hooks/exhaustive-deps

  // Fetch when dateTo changes (for date range updates)
  // Skip initial mount and skip if dateTo didn't actually change
  React.useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false
      return
    }

    const dateToChanged = prevDateToRef.current.getTime() !== dateTo.getTime()
    if (!dateToChanged) {
      return
    }

    prevDateToRef.current = dateTo
    fetchData(storeRef.current)
  }, [dateTo, fetchData])

  return (
    <ClockIn loading={loading} store={store} canReadOwnedAttendance={canReadOwnedAttendance}>
      <Outlet />
    </ClockIn>
  )
})

const EmployeePeriodRootPage = () => {
  const { employee } = useCurrentSession()
  const { stringDateFrom: startOn } = useAttendanceDateNavigator()

  return (
    <ContractsVersionProvider employeeId={employee?.id ?? '0'} activeDate={startOn}>
      <AttendancePoliciesProvider employeeId={employee?.id ?? '0'}>
        <PeriodContainer employee={employee} />
      </AttendancePoliciesProvider>
    </ContractsVersionProvider>
  )
}

export default EmployeePeriodRootPage
